/**
 * Idle-gated Independence Day fireworks — a bespoke canvas effect (not a falling
 * effect). Rockets launch from behind the Statue of Liberty (center-bottom),
 * rise, then burst into red/white/blue/gold sparks that arc, drift, and fade
 * with glowing trails. Spark colors come from the `--fw-*` theme vars.
 */
import React, { useEffect, useRef } from 'react';

import { Z } from '@shared/constants/zIndex';
import { useMediaQuery } from '@shared/hooks/useMediaQuery';

import { useIsIdle } from '../hooks/useIsIdle';

const IDLE_MS = 15000;
const GRAVITY = 0.03;
const FRICTION = 0.985;
const TAU = Math.PI * 2;
const MAX_ROCKETS = 6;
const FW_VARS = ['--fw-red', '--fw-white', '--fw-blue', '--fw-gold'];
const DEFAULT_COLOR = '#f8f9fa';
const FALLBACKS = ['#e63946', '#f8f9fa', '#4895ef', '#ffd166'];

interface Spark {
  x: number; y: number; vx: number; vy: number;
  life: number; decay: number; color: string; size: number;
}
interface Rocket { x: number; y: number; vy: number; burstY: number; color: string; }
interface Scene { rockets: Rocket[]; sparks: Spark[]; timer: number; }

const rand = (a: number, b: number): number => a + Math.random() * (b - a);

/** Resolve the four firework colors from the theme vars on the canvas. */
function paletteFor(canvas: HTMLCanvasElement): string[] {
  const s = getComputedStyle(canvas);
  return FW_VARS.map((v, i) => s.getPropertyValue(v).trim() || FALLBACKS[i] || DEFAULT_COLOR);
}

/** A new rocket rising from behind the statue toward a random burst height. */
function launchRocket(w: number, h: number, colors: string[]): Rocket {
  return {
    x: w * 0.5 + rand(-w * 0.08, w * 0.08),
    y: h,
    vy: -rand(6.5, 9.5),
    burstY: h * rand(0.12, 0.42),
    color: colors[Math.floor(rand(0, colors.length))] ?? DEFAULT_COLOR,
  };
}

/** Replace a rocket with a ring/spray of sparks at its position. */
function burst(r: Rocket, sparks: Spark[]): void {
  const count = Math.floor(rand(34, 72));
  const power = rand(2.2, 3.8);
  const tightRing = Math.random() < 0.5;
  for (let i = 0; i < count; i++) {
    const ang = (i / count) * TAU + rand(-0.08, 0.08);
    const spd = power * (tightRing ? rand(0.85, 1) : rand(0.35, 1));
    sparks.push({
      x: r.x, y: r.y,
      vx: Math.cos(ang) * spd, vy: Math.sin(ang) * spd,
      life: 1, decay: rand(0.008, 0.016),
      color: r.color, size: rand(1.3, 2.8),
    });
  }
}

/** Advance a rocket; returns false once it has reached its burst point. */
function stepRocket(r: Rocket): boolean {
  r.y += r.vy;
  r.vy += GRAVITY;
  return r.y > r.burstY && r.vy < 0;
}

/** Advance a spark under gravity + drag; returns false once it has faded. */
function stepSpark(s: Spark): boolean {
  s.vx *= FRICTION;
  s.vy = s.vy * FRICTION + GRAVITY;
  s.x += s.vx;
  s.y += s.vy;
  s.life -= s.decay;
  return s.life > 0;
}

/** Draw one additive glowing dot (a spark or rocket) at the given alpha. */
function drawDot(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, color: string, alpha: number): void {
  ctx.globalAlpha = Math.max(0, Math.min(1, alpha));
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, TAU);
  ctx.fill();
}

/** Paint one frame: fade prior frame for trails, spawn, then advance + draw. */
function renderScene(ctx: CanvasRenderingContext2D, w: number, h: number, scene: Scene, colors: string[]): void {
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = 'destination-out';
  ctx.fillStyle = 'rgba(0,0,0,0.14)';
  ctx.fillRect(0, 0, w, h);
  ctx.globalCompositeOperation = 'lighter';

  scene.timer -= 1;
  if (scene.timer <= 0 && scene.rockets.length < MAX_ROCKETS) {
    scene.rockets.push(launchRocket(w, h, colors));
    scene.timer = rand(22, 55);
  }
  scene.rockets = scene.rockets.filter(r => {
    const flying = stepRocket(r);
    if (flying) drawDot(ctx, r.x, r.y, 2, r.color, 1);
    else burst(r, scene.sparks);
    return flying;
  });
  scene.sparks = scene.sparks.filter(s => {
    const alive = stepSpark(s);
    if (alive) drawDot(ctx, s.x, s.y, s.size, s.color, s.life);
    return alive;
  });
}

/** Full-screen idle fireworks over the Patriotic backdrop. */
export const FireworksOverlay: React.FC = () => {
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const idle = useIsIdle(IDLE_MS, !reducedMotion);
  const active = !reducedMotion && idle;
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!active) return undefined;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return undefined;

    let width = (canvas.width = window.innerWidth);
    let height = (canvas.height = window.innerHeight);
    const colors = paletteFor(canvas);
    const scene: Scene = { rockets: [], sparks: [], timer: 8 };
    let raf = 0;
    const render = (): void => {
      renderScene(ctx, width, height, scene, colors);
      raf = requestAnimationFrame(render);
    };
    const onResize = (): void => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', onResize);
    raf = requestAnimationFrame(render);
    canvas.animate?.([{ opacity: 0 }, { opacity: 1 }], { duration: 1200, easing: 'ease-out' });

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
    };
  }, [active]);

  if (!active) return null;

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      data-testid="fireworks-overlay"
      className="pointer-events-none fixed inset-0 h-full w-full"
      style={{ zIndex: Z.AMBIENT }}
    />
  );
};

FireworksOverlay.displayName = 'FireworksOverlay';
